import { useEffect } from "react";

// Friction against casual copying of a reference page while it's open.
// Deliberately silent — no dialogs, nothing to screenshot and complain about,
// the action simply does not happen. This raises the cost of lifting the
// content; it cannot make it impossible, and nothing in a browser can.
// Everything is scoped to this screen and removed on unmount so the rest of
// the app is unaffected. Shared by every page built on the same "this is
// company IP, not a secret, but don't make it trivial to walk out the door
// with" footing — first built for Playbook, now also the Growth Roadmap.
export function useCopyProtection(isExempt) {
  useEffect(() => {
    // Superusers (or whoever the caller exempts) get the page unrestricted —
    // right-click, select, print, save. Returning before anything is
    // attached means there is nothing to clean up and no listener left
    // behind to interfere.
    if (isExempt) return undefined;
    const swallow = (e) => { e.preventDefault(); return false; };
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && ["s", "p", "c", "x", "u"].includes(k)) return swallow(e);
      if (k === "printscreen") {
        // Best effort: overwrite the clipboard so a captured frame is not
        // sitting there to be pasted.
        try { navigator.clipboard.writeText(""); } catch { /* not permitted, fine */ }
        return swallow(e);
      }
      return undefined;
    };
    document.addEventListener("contextmenu", swallow);
    document.addEventListener("copy", swallow);
    document.addEventListener("cut", swallow);
    document.addEventListener("dragstart", swallow);
    document.addEventListener("keydown", onKey);
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
    return () => {
      document.removeEventListener("contextmenu", swallow);
      document.removeEventListener("copy", swallow);
      document.removeEventListener("cut", swallow);
      document.removeEventListener("dragstart", swallow);
      document.removeEventListener("keydown", onKey);
      document.body.style.userSelect = prevSelect;
      document.body.style.webkitUserSelect = prevSelect;
    };
    // The caller's exemption flag (usually read off the live session) can
    // still be resolving on first paint — this has to re-run once it
    // settles, otherwise an exempt user stays locked out of their own page.
  }, [isExempt]);
}
